import { useEffect, useState, useRef } from "react";
import { Form, useActionData, useNavigation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { LanguageSwitcher } from "../../components/common/LanguageSwitcher";
import type { ActionResult } from "../../routes/actions";
import "./auth.css";

export default function ForgotPasswordPage() {
  const { t } = useTranslation();
  const navigation = useNavigation();
  const actionData = useActionData() as ActionResult | undefined;
  const isSubmitting = navigation.state === "submitting";
  const [emailSent, setEmailSent] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState("");
  const processedActionRef = useRef<ActionResult | null>(null);

  useEffect(() => {
    if (!actionData || actionData === processedActionRef.current) return;

    processedActionRef.current = actionData;

    if (actionData.success) {
      setEmailSent(true);
      toast.success(t("auth.forgotPassword.success"));
    } else if (actionData.error) {
      if (actionData.error.includes("Too many")) {
        toast.error(t("errors.networkError"));
      } else {
        setEmailSent(true);
      }
    }
  }, [actionData, t]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    const form = e.currentTarget;
    const formData = new FormData(form);
    setSubmittedEmail(formData.get("email") as string);
  };

  const handleTryAnotherEmail = () => {
    setEmailSent(false);
    setSubmittedEmail("");
    processedActionRef.current = null;
  };

  if (emailSent) {
    return (
      <div className="auth-container">
        <div className="auth-language-switcher">
          <LanguageSwitcher variant="standalone" />
        </div>
        <div className="auth-card">
          <div className="auth-header">
            <h1>{t("auth.forgotPassword.checkEmail", "Check Your Email")}</h1>
          </div>

          <div className="success-message">
            <strong>{t("auth.forgotPassword.emailSent", "Email Sent!")}</strong>
            <p style={{ marginTop: "0.5rem", marginBottom: 0 }}>
              {t(
                "auth.forgotPassword.emailSentMessage",
                "If an account exists for {{email}}, you will receive password reset instructions shortly.",
                { email: submittedEmail },
              )}
            </p>
          </div>

          <div className="info-message">
            <p style={{ margin: 0 }}>
              <strong>{t("auth.forgotPassword.didntReceive", "Didn't receive the email?")}</strong>
            </p>
            <ul style={{ marginTop: "0.5rem", marginBottom: 0, paddingLeft: "1.5rem" }}>
              <li>{t("auth.forgotPassword.checkSpam", "Check your spam or junk folder")}</li>
              <li>
                {t(
                  "auth.forgotPassword.checkEmail2",
                  "Make sure you entered the correct email address",
                )}
              </li>
              <li>
                {t("auth.forgotPassword.waitMinutes", "Wait a few minutes for the email to arrive")}
              </li>
            </ul>
          </div>

          <div className="action-buttons">
            <button type="button" className="btn-secondary" onClick={handleTryAnotherEmail}>
              {t("auth.forgotPassword.tryAnotherEmail", "Try another email")}
            </button>
            <Link
              to="/login"
              className="btn-primary"
              style={{ textAlign: "center", textDecoration: "none", display: "block" }}
            >
              {t("auth.forgotPassword.backToLogin")}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-language-switcher">
        <LanguageSwitcher variant="standalone" />
      </div>
      <div className="auth-card">
        <div className="auth-header">
          <h1>{t("auth.forgotPassword.title")}</h1>
          <p>{t("auth.forgotPassword.subtitle")}</p>
        </div>

        <Form method="post" className="auth-form" onSubmit={handleSubmit}>
          <div className={`form-group ${actionData?.fieldErrors?.email ? "error" : ""}`}>
            <label htmlFor="email">{t("auth.forgotPassword.emailLabel")}</label>
            <input
              id="email"
              name="email"
              type="email"
              placeholder={t("auth.forgotPassword.emailPlaceholder")}
              autoComplete="email"
            />
            {actionData?.fieldErrors?.email && (
              <span className="form-error">{actionData.fieldErrors.email}</span>
            )}
          </div>

          <button type="submit" className="btn-primary" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <span className="loading-spinner"></span> {t("auth.forgotPassword.submitting")}
              </>
            ) : (
              t("auth.forgotPassword.submitButton")
            )}
          </button>
        </Form>

        <div className="auth-links">
          <Link to="/login" className="auth-link">
            {t("auth.forgotPassword.rememberPassword", "Remember your password? Sign in")}
          </Link>
        </div>
      </div>
    </div>
  );
}
